import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateDashboardLayout from '../components/layouts/CandidateDashboardLayout';
import Icon from '../components/atoms/Icon';
import { request } from '../services/api';
import SessionManager from '../utils/sessionManager';

const CandidateTransactionsPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [courseTransactions, setCourseTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'credits', 'courses'

  const session = SessionManager.getSession('candidate');
  const candidateId = session?.profile?.id || session?.user_id;

  useEffect(() => {
    if (!candidateId) {
      navigate('/login');
      return;
    }
    loadTransactions();
    loadBalance();
  }, [candidateId, navigate]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      
      // Load credit transactions
      const creditData = await request(`/payments/credits/orders?candidate_id=${candidateId}&limit=50`);
      setTransactions(creditData);
      
      // Load course transactions
      let courseData = { courses: [] };
      try {
        courseData = await request(`/course-teaching/my-courses`);
        setCourseTransactions(courseData.courses || []);
      } catch (error) {
        console.error('Error loading course transactions:', error);
        setCourseTransactions([]);
      }
      
      // Combine all transactions
      const combined = [
        ...creditData.map(t => ({ ...t, type: 'credit' })),
        ...(courseData?.courses || []).map(c => ({
          id: c.purchase_id || c.id,
          type: 'course',
          created_at: c.purchased_at || c.enrolledAt,
          course_title: c.title,
          amount_inr: c.price,
          status: 'paid'
        }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setAllTransactions(combined);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBalance = async () => {
    try {
      const data = await request(`/credits/balance?candidate_id=${candidateId}`);
      setCurrentBalance(data.balance);
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDisplayTransactions = () => {
    switch (activeTab) {
      case 'credits':
        return transactions.map(t => ({ ...t, type: 'credit' }));
      case 'courses':
        return courseTransactions.map(c => ({
          id: c.purchase_id || c.id,
          type: 'course',
          created_at: c.purchased_at || c.enrolledAt,
          course_title: c.title,
          amount_inr: c.price,
          status: 'paid'
        }));
      default:
        return allTransactions;
    }
  };

  const displayTransactions = getDisplayTransactions();

  if (loading) {
    return (
      <CandidateDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  return (
    <CandidateDashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
          <p className="text-gray-600 mt-1">View your payment history and credit purchases</p>
        </div>

        {/* Balance Card */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 mb-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-100 mb-2">Current Balance</p>
              <p className="text-4xl font-bold">{currentBalance} Credits</p>
            </div>
            <button
              onClick={() => navigate('/payment')}
              className="px-6 py-3 bg-white text-teal-700 rounded-lg hover:bg-teal-50 font-medium flex items-center gap-2"
            >
              <Icon name="add" size={20} />
              Buy Credits
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">{allTransactions.length}</p>
            <p className="text-sm text-gray-600">Total Transactions</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-2xl font-bold text-green-600">
              {allTransactions.filter(t => t.status === 'paid' || t.status === 'completed').length}
            </p>
            <p className="text-sm text-gray-600">Successful</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">
              ₹{allTransactions.filter(t => t.status === 'paid' || t.status === 'completed').reduce((sum, t) => sum + parseFloat(t.amount_inr || 0), 0).toFixed(2)}
            </p>
            <p className="text-sm text-gray-600">Total Spent</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-t-xl border border-gray-200 border-b-0">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'all'
                  ? 'text-teal-600 border-b-2 border-teal-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Transactions ({allTransactions.length})
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'credits'
                  ? 'text-teal-600 border-b-2 border-teal-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Credits ({transactions.length})
            </button>
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'courses'
                  ? 'text-teal-600 border-b-2 border-teal-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Courses ({courseTransactions.length})
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
          {displayTransactions.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="receipt_long" size={64} className="text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transactions Yet</h3>
              <p className="text-gray-600 mb-6">
                {activeTab === 'courses' 
                  ? 'Enroll in courses to see your course purchases'
                  : 'Purchase credits to see your transaction history'}
              </p>
              <button
                onClick={() => navigate(activeTab === 'courses' ? '/courses' : '/payment')}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                {activeTab === 'courses' ? 'Browse Courses' : 'Buy Credits'}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {displayTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(transaction.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {transaction.type === 'course' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            <Icon name="school" size={14} />
                            Course
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            <Icon name="account_balance_wallet" size={14} />
                            Credits
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {transaction.type === 'course' ? (
                          <div>
                            <div className="font-medium">{transaction.course_title}</div>
                            <div className="text-xs text-gray-500">Course Purchase</div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-medium">{transaction.credits_to_purchase} Credits</div>
                            <div className="text-xs text-gray-500 font-mono">{transaction.razorpay_order_id || transaction.id}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₹{transaction.amount_inr}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </CandidateDashboardLayout>
  );
};

export default CandidateTransactionsPage;
